import { useEffect, useRef } from 'react';
import { getPresignedUrlExpiry } from '@/utils/presignedUrl';

// Re-mint the URL this long before it actually lapses, so the browser never
// fetches an already-expired URL (which S3 rejects with 403).
const EXPIRY_REFRESH_LEAD_MS = 2 * 60 * 1000;

/**
 * Presigned S3 / CloudFront URLs expire (e.g. 24h resume / 6h docx preview). A
 * viewer left open past that window fetches an expired URL and S3 responds 403
 * ("unable to load"). Given the URL(s) currently rendered and a way to re-mint
 * them (`onRefresh` — usually a React Query `refetch` owned by the data layer,
 * since the viewer itself can't re-sign the URL), this schedules a refresh just
 * before the soonest expiry, or immediately (once per URL set) if it is already
 * expired — e.g. served from a warm cache. No-ops when `onRefresh` is absent or
 * no URL carries a parseable expiry, so callers opt in with zero behaviour change.
 */
export const usePresignedUrlRefresh = (
  urls: Array<string | null | undefined>,
  onRefresh?: () => void
): void => {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const lastRefreshedKeyRef = useRef<string | undefined>(undefined);

  // Stable primitive key so the effect re-runs only when the URLs actually change.
  const key = urls.join('|');

  useEffect(() => {
    if (!onRefreshRef.current) return;

    const expiries = key
      .split('|')
      .map(getPresignedUrlExpiry)
      .filter((e): e is number => e !== null);
    if (!expiries.length) return;

    const delay = Math.min(...expiries) - Date.now() - EXPIRY_REFRESH_LEAD_MS;
    if (delay <= 0) {
      // Already at/near expiry — re-mint once per URL set, so a backend that
      // hands back an equally-stale URL can't loop us.
      if (lastRefreshedKeyRef.current === key) return;
      lastRefreshedKeyRef.current = key;
      onRefreshRef.current();
      return;
    }

    const timer = setTimeout(() => onRefreshRef.current?.(), delay);
    return () => clearTimeout(timer);
  }, [key]);
};
